import mixpanel from 'mixpanel-browser';
import { logger } from '../utils/logger';

type MixpanelConfig = {
  token: string;
  debug?: boolean;
  trackPageview?: boolean;
  persistenceType?: 'localStorage' | 'cookie';
  autocapture?: boolean;
  recordSessionsPercent?: number;
  apiHost?: string;
};

type UserProperties = {
  telegramId?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  walletAddress?: string;
  [key: string]: string | number | boolean | undefined;
};

type EventProperties = Record<string, string | number | boolean | null | undefined>;

let isInitialized = false;

/**
 * Initialize Mixpanel with the given configuration.
 * Should be called once at app startup.
 */
export function initMixpanel(config: MixpanelConfig): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (isInitialized) {
    logger.warn('[Mixpanel] Already initialized');
    return;
  }

  const { 
    token, 
    debug = false, 
    trackPageview = true, 
    persistenceType = 'localStorage',
    autocapture = true,
    recordSessionsPercent = 100,
    apiHost = 'https://api-eu.mixpanel.com',
  } = config;

  if (!token) {
    logger.warn('[Mixpanel] No token provided, skipping initialization');
    return;
  }

  mixpanel.init(token, {
    debug,
    track_pageview: trackPageview,
    persistence: persistenceType,
    ignore_dnt: false,
    autocapture,
    record_sessions_percent: recordSessionsPercent,
    api_host: apiHost,
  });

  isInitialized = true;
  logger.log('[Mixpanel] Initialized successfully');
}

/**
 * Check if Mixpanel is initialized
 */
export function isMixpanelInitialized(): boolean {
  return isInitialized;
}

/**
 * Track an event with optional properties
 */
export function trackEvent(eventName: string, properties?: EventProperties): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping track:', eventName);
    return;
  }

  mixpanel.track(eventName, properties);
}

/**
 * Identify a user with their unique ID
 */
export function identifyUser(userId: string): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping identify');
    return;
  }

  mixpanel.identify(userId);
}

/**
 * Set user properties (super properties that persist across events)
 */
export function setUserProperties(properties: UserProperties): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping setUserProperties');
    return;
  }

  mixpanel.people.set(properties);
}

/**
 * Set user properties only once (won't overwrite existing values)
 */
export function setUserPropertiesOnce(properties: UserProperties): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping setUserPropertiesOnce');
    return;
  }

  mixpanel.people.set_once(properties);
}

/**
 * Register super properties that are sent with every event
 */
export function registerSuperProperties(properties: EventProperties): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping registerSuperProperties');
    return;
  }

  mixpanel.register(properties);
}

/**
 * Register super properties once (won't overwrite existing values)
 */
export function registerSuperPropertiesOnce(properties: EventProperties): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping registerSuperPropertiesOnce');
    return;
  }

  mixpanel.register_once(properties);
}

/**
 * Reset user identity (call on logout)
 */
export function resetUser(): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping reset');
    return;
  }

  mixpanel.reset();
}

/**
 * Track a page view with optional properties
 */
export function trackPageView(pageName: string, properties?: EventProperties): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping trackPageView');
    return;
  }

  mixpanel.track('Page View', {
    page: pageName,
    ...properties,
  });
}

/**
 * Increment a numeric user property
 */
export function incrementUserProperty(property: string, value: number = 1): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping incrementUserProperty');
    return;
  }

  mixpanel.people.increment(property, value);
}

/**
 * Track timing of an operation
 */
export function timeEvent(eventName: string): void {
  if (!isInitialized) {
    logger.warn('[Mixpanel] Not initialized, skipping timeEvent');
    return;
  }

  mixpanel.time_event(eventName);
}

/**
 * Opt user out of tracking
 */
export function optOut(): void {
  if (!isInitialized) {
    return;
  }

  mixpanel.opt_out_tracking();
}

/**
 * Opt user back into tracking
 */
export function optIn(): void {
  if (!isInitialized) {
    return;
  }

  mixpanel.opt_in_tracking();
}

/**
 * Check if user has opted out
 */
export function hasOptedOut(): boolean {
  if (!isInitialized) {
    return false;
  }

  return mixpanel.has_opted_out_tracking();
}

export const mixpanelService = {
  init: initMixpanel,
  isInitialized: isMixpanelInitialized,
  track: trackEvent,
  identify: identifyUser,
  setUserProperties,
  setUserPropertiesOnce,
  registerSuperProperties,
  registerSuperPropertiesOnce,
  reset: resetUser,
  trackPageView,
  incrementUserProperty,
  timeEvent,
  optOut,
  optIn,
  hasOptedOut,
};

